import React from "react";

import { Loader2 } from "lucide-react";

import Button from "../../Components/Button";
import CommonInput from "../../Components/Input";
import Modal from "../../Components/Modal";
import TextArea from "../../Components/TextArea";
import {
  formalDueMaxInputValue,
  formalDueMinInputValue,
} from "../../Utils/Helper/assessmentDueDefaults";
import { normalizeTeamAssessmentDisplayName } from "../../Utils/Helper/assessmentOriginHelpers";

export type FormalRoundTarget = {
  /** Hub convert: team assessment id. Level Mix create: account id (list key). */
  assessment_id: string;
  account_id?: string;
  account_name?: string | null;
  name?: string | null;
  title?: string | null;
  /** Parent Formal/Team assessment to clone questions from (Level Mix create). */
  source_assessment_id?: string;
};

export const getFormalRoundDisplayLine = (a: FormalRoundTarget): string => {
  const acc = a.account_name || a.name || "Assessment";
  const rawTitle = (a.title || a.name || "").trim();
  if (!rawTitle || rawTitle === acc) return acc;
  const slug = normalizeTeamAssessmentDisplayName(
    rawTitle,
    "Team Assessment",
    a.account_name
  );
  return `${acc} · ${slug}`;
};

export const buildDefaultFormalName = (
  targets: FormalRoundTarget[]
): string => {
  const names = targets
    .map(t => t.account_name || t.name)
    .filter(Boolean) as string[];
  return names.length ? `Formal — ${names.join(", ")}` : "Formal AL assessment";
};

export const buildDefaultFormalDescription = (count: number): string =>
  `Formal assessment for ${count} selected account${count === 1 ? "" : "s"}`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  targets: FormalRoundTarget[];
  name: string;
  description: string;
  dueOn: string;
  isSubmitting: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onDueOnChange: (value: string) => void;
  onConfirm: () => void;
}

const StartFormalRoundModal: React.FC<Props> = ({
  isOpen,
  onClose,
  targets,
  name,
  description,
  dueOn,
  isSubmitting,
  onNameChange,
  onDescriptionChange,
  onDueOnChange,
  onConfirm,
}) => (
  <Modal
    isOpen={isOpen}
    toggle={onClose}
    title="Start formal round"
    size="lg"
    footer={
      <div className="flex justify-end gap-3">
        <Button variant="outline-primary" onClick={onClose}>
          Cancel
        </Button>
        <button
          type="button"
          disabled={isSubmitting}
          onClick={onConfirm}
          className="inline-flex h-10 items-center justify-center rounded-lg bg-orange-400 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            "Start formal"
          )}
        </button>
      </div>
    }
  >
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-gray-700">
        Combine {targets.length} selected account
        {targets.length === 1 ? "" : "s"} into one formal assessment round.
        Configured account stakeholders (sponsors, account team, other
        executives) will receive formal assignments.
      </p>

      <div className="rounded-lg border border-gray-200 bg-gray-50/60 px-4 py-3">
        <ul className="m-0 list-none space-y-2 p-0">
          {targets.map(a => (
            <li
              key={a.assessment_id}
              className="text-sm font-medium text-gray-800"
            >
              {getFormalRoundDisplayLine(a)}
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-4">
        <CommonInput
          label="Assessment name"
          name="formalAssessmentName"
          value={name}
          onChange={e => onNameChange(e.target.value)}
          required
        />
        <TextArea
          label="Description"
          name="formalAssessmentDescription"
          value={description}
          onChange={e => onDescriptionChange(e.target.value)}
          rows={3}
        />
        <CommonInput
          label="Due date"
          name="formalAssessmentDueOn"
          type="date"
          value={dueOn}
          onChange={e => onDueOnChange(e.target.value)}
          required
          min={formalDueMinInputValue()}
          max={formalDueMaxInputValue()}
          lang="en-GB"
        />
      </div>
    </div>
  </Modal>
);

export default StartFormalRoundModal;
